import { Checkbox, Chip, ImageListItem, ImageListItemBar, Skeleton, Stack } from "@mui/material";
import type { FC } from "react";
import { useEffect, useState } from "react";
import type { ImageFlavour } from "../../publicPackages/publicPackagesApi";
import ImagePreviewMenu from "./ImagePreviewMenu";
import { storageUrlWithCredentials } from "./storageUrlWithCredentials";

export interface Image {
  readonly id: string;
  readonly name: string;
  readonly tags: string[];
  readonly products: string[];
  readonly isPrivate: boolean;
  readonly info?: string;
  readonly source?: string;
  readonly thumbnail: {
    readonly url: string;
  };
  readonly original: {
    readonly url: string;
  };
  readonly www: {
    readonly url: string;
  };
  readonly flavours: Partial<Record<ImageFlavour, { readonly url: string }>>;
}

interface Props {
  readonly image: Image;
  readonly width: number;
  readonly height?: number;
  readonly selectMode?: boolean;
  readonly selected?: boolean;
  readonly onToggleSelection?: () => void;
  readonly onDelete?: () => void | Promise<void>;
  readonly onEdit?: () => void | Promise<void>;
  readonly onCrop?: () => void;
  readonly onUpdatePrivacy?: (isPrivate: boolean) => void | Promise<void>;
}

const ImagePreview: FC<Props> = ({
  image,
  width,
  height = width,
  selectMode,
  selected,
  onToggleSelection,
  onDelete,
  onEdit,
  onCrop,
  onUpdatePrivacy,
}) => {
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);

  const openImage = async () => {
    const signedUrl = await storageUrlWithCredentials(image.original.url);
    window.open(signedUrl, "_blank");
  };

  useEffect(() => {
    storageUrlWithCredentials(image.thumbnail.url).then((url) => setThumbnailUrl(url ?? null));
  }, [image.thumbnail.url]);
  return (
    <ImageListItem sx={{ width, height }}>
      {thumbnailUrl ? (
        <img
          srcSet={thumbnailUrl}
          src={thumbnailUrl}
          alt={image.name}
          loading="lazy"
          onClick={openImage}
          height={height}
          width={width}
          style={{ objectFit: "cover", overflow: "hidden" }}
        />
      ) : (
        <Skeleton variant="rectangular" width={width} height={height} />
      )}
      <ImageListItemBar
        subtitle={
          <Stack
            direction="row"
            sx={{
              gap: 1,
            }}
          >
            {image.tags.map((t) => (
              <Chip variant="filled" size="small" color="primary" key={t} label={t} />
            ))}
          </Stack>
        }
        actionIcon={
          selectMode ? (
            <Checkbox
              checked={selected}
              sx={{ color: "white" }}
              onChange={onToggleSelection}
              slotProps={{
                input: { "aria-label": "Zaznacz" },
              }}
            />
          ) : onDelete || onEdit || onUpdatePrivacy ? (
            <ImagePreviewMenu
              isPrivate={image.isPrivate}
              onDelete={onDelete}
              onEdit={onEdit}
              onCrop={onCrop}
              onUpdatePrivacy={onUpdatePrivacy}
            />
          ) : null
        }
      />
    </ImageListItem>
  );
};

export default ImagePreview;
